import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "./tokens.css";
import "./auth.css";  // s204 — la misma puerta que el login del socio y del comercio
// s201 · la base viene de api.js como en el resto del front. Esta era la copia que
// sobrevivio a s184: con el entorno de desarrollo, una copia fija habria dejado esta
// pagina pidiendo el reset en Live mientras todo lo demas corria en pruebas.
import { API_BASE } from "./api";

/**
 * ForgotPasswordPage - Página de solicitud de reset de contraseña para NXT Reserve
 *
 * Flujo:
 * 1. Usuario ingresa su email
 * 2. Submit a Bubble backend workflow request_password_reset
 * 3. Mensaje genérico de éxito (por seguridad - no revela si el email existe)
 * 4. Usuario revisa su inbox y sigue el link a la página nativa reset_pw de Bubble
 *    (el token de reset solo se consume ahí; no existe página de reset en React)
 *
 * Standalone: lee localStorage 'nxt-lang' directamente, no requiere imports de App.jsx
 */

// Hook standalone para leer el idioma sin depender de LangContext de App.jsx
function useLangStandalone() {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("nxt-lang") || "es";
    } catch {
      return "es";
    }
  });

  useEffect(() => {
    const handleStorage = () => {
      try {
        setLang(localStorage.getItem("nxt-lang") || "es");
      } catch {
        setLang("es");
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  return lang;
}

// Diccionario de strings bilingües
const T = {
  es: {
    title: "Recuperar contraseña",
    subtitle:
      "Ingresa tu email y te enviaremos un link para crear una nueva contraseña.",
    email: "Email",
    placeholder: "tu@email.com",
    submit: "Enviar link de recuperación",
    submitLoading: "Enviando...",
    backToLogin: "← Volver al login",
    successMsg:
      "Si el email existe, te enviamos un link de recuperación. Revisa tu bandeja (y la carpeta de spam).",
    errInvalidEmail: "Ingresa un email válido",
    errConnection: "Error de conexión. Intenta de nuevo.",
  },
  en: {
    title: "Reset password",
    subtitle:
      "Enter your email and we'll send you a link to create a new password.",
    email: "Email",
    placeholder: "your@email.com",
    submit: "Send recovery link",
    submitLoading: "Sending...",
    backToLogin: "← Back to login",
    successMsg:
      "If that email exists, we sent you a reset link. Check your inbox (and spam folder).",
    errInvalidEmail: "Enter a valid email",
    errConnection: "Connection error. Try again.",
  },
};

export default function ForgotPasswordPage() {
  const lang = useLangStandalone();
  const t = T[lang] || T.es;
  // s204 · «Volver al login» llevaba siempre al login del SOCIO, aunque viniera un
  // comercio. El login del comercio manda `state.volver`; solo se aceptan las dos
  // puertas conocidas (nada que venga de afuera decide adonde se navega).
  const location = useLocation();
  const volver = location.state?.volver === "/merchant/login" ? "/merchant/login" : "/login";

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null); // { type: "success" | "error", text: string }
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    // Validación básica de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setMessage({ type: "error", text: t.errInvalidEmail });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/wf/request_password_reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email }),
      });

      // Por seguridad SIEMPRE mostramos mensaje genérico de éxito
      // (no revelar si el email existe o no en la base)
      // Solo mostramos error si hay falla de red real
      if (!response.ok && response.status >= 500) {
        throw new Error("Server error");
      }

      setMessage({ type: "success", text: t.successMsg });
      setSubmitted(true);
    } catch (err) {
      console.error("Forgot password error:", err);
      setMessage({ type: "error", text: t.errConnection });
    } finally {
      setLoading(false);
    }
  };

  // s204 · esta pagina entra a LA PUERTA (auth.css), como los dos logins. Es la
  // pantalla que ve el comercio invitado cuando pide o crea su clave, y era una
  // tarjeta con sombra sobre gris, con otra tipografia de boton y otro radio.
  // Mismos textos que antes.
  return (
    <div className="auth">
      <div className="auth__barra">
        <Link to={volver} className="auth__marca" aria-label="NXT Reserve">
          <span className="auth__marca-barra" />
          <span className="auth__marca-texto">
            <span className="auth__marca-nxt">NXT</span>
            <span className="auth__marca-reserve">RESERVE</span>
          </span>
        </Link>
      </div>

      <div className="auth__centro">
        <div className="auth__caja">
          <div className="auth__saludo">
            <h1 className="auth__titulo">{t.title}</h1>
            <p className="auth__bajada">{t.subtitle}</p>
          </div>

          {message && (
            <div className={message.type === "success" ? "auth__aviso" : "auth__error"}
              role={message.type === "success" ? "status" : "alert"}>
              {message.text}
            </div>
          )}

          {!submitted && (
            <form onSubmit={handleSubmit}>
              <div className="auth__campo">
                <label className="auth__etiqueta" htmlFor="nxt-olvido-email">{t.email}</label>
                <input
                  className="auth__entrada"
                  id="nxt-olvido-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.placeholder}
                  required
                  disabled={loading}
                  autoFocus
                />
              </div>

              <button className="auth__boton" type="submit" disabled={loading}>
                {loading ? t.submitLoading : t.submit}
              </button>
            </form>
          )}

          <Link className="auth__menor" to={volver}>
            {t.backToLogin}
          </Link>
        </div>
      </div>
    </div>
  );
}
